import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import {
  ArrowRight, ArrowLeft, CheckCircle2, AlertTriangle, Calendar, Clock, Phone,
  MessageSquare, Bell, RefreshCw, Wallet, BarChart3, Sparkles, Play, Loader2,
  ChevronRight, Check, X, Users, TrendingUp, PhoneCall, Mic, Euro, FileText
} from "lucide-react";
import SmartRecordsStep from "./demos/SmartRecordsStep";
import DentalSuiteStep from "./demos/DentalSuiteStep";

// ============================================================
// DEMO DATA (odontoiatrico)
// ============================================================
const OPERATORS = [
  { name: "Dr. Marco Rinaldi", role: "Implantologia", color: "bg-violet-500" },
  { name: "Dr.ssa Laura Greco", role: "Igiene", color: "bg-emerald-500" },
  { name: "Dr. Paolo Conti", role: "Ortodonzia", color: "bg-amber-500" },
  { name: "Reception", role: "Accoglienza", color: "bg-slate-500" },
];

const CHAIRS = ["Poltrona 1", "Poltrona 2", "Sala Igiene"];

const APPOINTMENTS = [
  { time: "09:00", chair: 0, op: 1, service: "Igiene", patient: "Anna Russo", color: "bg-emerald-100 dark:bg-emerald-900/40 border-emerald-500" },
  { time: "09:30", chair: 1, op: 0, service: "Prima visita", patient: "Luca Greco", color: "bg-blue-100 dark:bg-blue-900/40 border-blue-500" },
  { time: "10:00", chair: 2, op: 1, service: "Controllo", patient: "Maria Costa", color: "bg-emerald-100 dark:bg-emerald-900/40 border-emerald-500" },
  { time: "11:00", chair: 0, op: 0, service: "Impianto", patient: "Giuseppe Neri", color: "bg-violet-100 dark:bg-violet-900/40 border-violet-500" },
  { time: "15:30", chair: 1, op: 2, service: "Ortodonzia", patient: "Sofia Bruno", color: "bg-amber-100 dark:bg-amber-900/40 border-amber-500" },
];

const SERVICES = [
  { name: "Prima visita gratuita", duration: 30, price: 0, highlight: true },
  { name: "Igiene dentale", duration: 45, price: 80 },
  { name: "Controllo periodico", duration: 20, price: 35 },
  { name: "Sbiancamento", duration: 60, price: 250 },
  { name: "Implantologia — consulto", duration: 30, price: 0 },
  { name: "Ortodonzia — consulto", duration: 30, price: 0 },
];

const INACTIVE_PATIENTS = [
  { name: "Mario Rossi", last: "ultima igiene 7 mesi fa", avatar: "MR" },
  { name: "Elisa Greco", last: "controllo 9 mesi fa", avatar: "EG" },
  { name: "Luca Neri", last: "richiamo ortodonzia", avatar: "LN" },
];

const QUOTES = [
  { patient: "Fabio Marini", treatment: "Impianto", amount: 1800, days: 8 },
  { patient: "Chiara Verdi", treatment: "Allineatori", amount: 2400, days: 10 },
  { patient: "Davide Palma", treatment: "Sbiancamento", amount: 350, days: 5 },
];

const TOTAL_STEPS = 11;

// ============================================================
// Main component
// ============================================================
const DemoOdonto = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [sessionId] = useState(() => `odo_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const stepStartRef = useRef(Date.now());

  // Track events
  const track = async (event, extra = {}) => {
    try {
      await axios.post(`${API}/demo/events/track`, {
        session_id: sessionId,
        variant: "odonto",
        event,
        step,
        duration_ms: Date.now() - stepStartRef.current,
        data: extra,
      }).catch(() => {});
    } catch {}
  };

  // Start tracking
  useEffect(() => {
    track("demo_started", { source: "page_load" });
    // track exit
    const onUnload = () => {
      navigator.sendBeacon?.(
        `${API}/demo/events/track`,
        new Blob([JSON.stringify({
          session_id: sessionId, variant: "odonto", event: "exit_step",
          step, duration_ms: Date.now() - stepStartRef.current,
        })], { type: "application/json" })
      );
    };
    window.addEventListener("beforeunload", onUnload);
    return () => window.removeEventListener("beforeunload", onUnload);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    stepStartRef.current = Date.now();
  }, [step]);

  const next = () => {
    track("step_completed", { from_step: step });
    setStep(s => Math.min(s + 1, TOTAL_STEPS - 1));
  };
  const prev = () => setStep(s => Math.max(s - 1, 0));

  const clickTrial = async (source) => {
    await track("clicked_trial", { source, plan: "dental_pro" });
    navigate("/onboarding?sector=dentista&prefill=demo-odonto&plan=dental_pro");
  };
  const clickCall = async (source) => {
    await track("clicked_call", { source });
  };

  const progress = (Math.min(step + 1, TOTAL_STEPS) / TOTAL_STEPS) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-slate-100" data-testid="demo-odonto-page">
      {/* Top progress bar */}
      <div className="sticky top-0 z-50 bg-slate-950/90 backdrop-blur border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-4">
          <Link to="/" className="text-xs text-slate-400 hover:text-white transition-colors hidden sm:flex items-center gap-1">
            <ArrowLeft className="w-3 h-3" /> SmartBooker
          </Link>
          <div className="flex-1 flex items-center gap-3">
            <span className="text-xs text-slate-400 whitespace-nowrap" data-testid="demo-odonto-step-label">
              {Math.min(step + 1, TOTAL_STEPS)}/{TOTAL_STEPS}
            </span>
            <div className="flex-1 h-1.5 rounded-full bg-slate-800 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-500 transition-all duration-500"
                style={{ width: `${progress}%` }} data-testid="demo-odonto-progress" />
            </div>
            <span className="text-[10px] text-slate-500 hidden md:inline">Demo completa in 2 min</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 sm:py-12">
        {step === 0 && <StepCover next={next} clickTrial={clickTrial} />}
        {step === 1 && <DentalSuiteStep />}
        {step === 2 && <StepProblem next={next} />}
        {step === 3 && <SmartRecordsStep sector="odonto" onCta={() => clickTrial("smart_records_step")} />}
        {step === 4 && <StepCalendar next={next} />}
        {step === 5 && <StepBooking next={next} />}
        {step === 6 && <StepWhatsApp next={next} />}
        {step === 7 && <StepReminder next={next} />}
        {step === 8 && <StepRecall next={next} />}
        {step === 9 && <StepQuotes next={next} />}
        {step === 10 && <StepAnalytics next={next} />}
        {step === 11 && <StepClosing clickTrial={clickTrial} clickCall={clickCall} />}
        {/* Nav */}
        {step > 0 && step < 10 && (
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-800">
            <Button variant="ghost" onClick={prev} className="text-slate-400 hover:text-white rounded-full" data-testid="demo-odonto-prev">
              <ArrowLeft className="w-4 h-4 mr-1" /> Indietro
            </Button>
            <Button
              onClick={step === 10 ? () => setStep(11) : next}
              className="rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
              data-testid="demo-odonto-next"
            >
              {step === 9 ? "Vedi i risultati" : step === 10 ? "Fine demo" : "Avanti"} <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Step 0 - Cover
// ============================================================
const StepCover = ({ next, clickTrial }) => (
  <div className="text-center space-y-6 max-w-4xl mx-auto" data-testid="demo-odonto-cover">
    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-cyan-500/40 bg-cyan-500/10 text-xs font-semibold text-cyan-300">
      <Sparkles className="w-3.5 h-3.5" /> DEMO INTERATTIVA · 2 MINUTI
    </div>
    <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold leading-tight">
      La piattaforma che <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">automatizza e fa crescere</span> il tuo centro odontoiatrico
    </h1>
    <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto">
      Prenotazioni, recall pazienti, WhatsApp, agenda e no-show in un solo sistema.
    </p>
    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4">
      <Button
        onClick={next}
        className="rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white h-12 px-7 text-base font-semibold shadow-xl shadow-cyan-500/30"
        data-testid="demo-odonto-start"
      >
        <Play className="w-5 h-5 mr-2" /> Guarda demo guidata
      </Button>
      <Button
        onClick={() => clickTrial("cover")}
        variant="outline"
        className="rounded-full border-slate-600 text-slate-200 hover:bg-slate-800 h-12 px-7 text-base"
        data-testid="demo-odonto-trial-cover"
      >
        Attiva prova gratuita
      </Button>
    </div>
    <div className="flex items-center justify-center gap-4 text-[11px] text-slate-500 pt-2 flex-wrap">
      <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Nessuna carta richiesta</span>
      <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Attivazione immediata</span>
      <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Facile da usare per reception</span>
    </div>
    <DemoHeroVisual />
  </div>
);

const DemoHeroVisual = () => (
  <div className="mt-10 relative rounded-3xl border border-slate-700/50 bg-gradient-to-br from-slate-800/60 to-slate-900/60 backdrop-blur-sm p-4 sm:p-8 shadow-2xl">
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-left">
      {[
        { icon: Users, label: "Nuovi pazienti", value: "+18", color: "text-blue-400" },
        { icon: Calendar, label: "Agenda piena", value: "+22%", color: "text-emerald-400" },
        { icon: Bell, label: "No-show", value: "-37%", color: "text-amber-400" },
        { icon: Euro, label: "Entrate extra", value: "€8.460", color: "text-violet-400" },
      ].map((k, i) => (
        <div key={i} className="p-3 sm:p-4 rounded-xl bg-slate-900/70 border border-slate-700/40">
          <k.icon className={`w-4 h-4 sm:w-5 sm:h-5 ${k.color} mb-1.5`} />
          <p className="text-[10px] sm:text-xs text-slate-500">{k.label}</p>
          <p className={`text-lg sm:text-2xl font-bold ${k.color}`}>{k.value}</p>
        </div>
      ))}
    </div>
  </div>
);

// ============================================================
// Step 1 - Problem
// ============================================================
const StepProblem = ({ next }) => (
  <StepCard testId="demo-odonto-problem">
    <div className="flex items-center gap-2 text-amber-400 text-xs font-semibold mb-3">
      <AlertTriangle className="w-4 h-4" /> IL PROBLEMA
    </div>
    <h2 className="text-2xl sm:text-3xl font-bold mb-4">
      Ogni giorno il tuo studio perde tempo e pazienti tra:
    </h2>
    <div className="grid sm:grid-cols-2 gap-3 mt-6">
      {[
        "Chiamate continue che bloccano la reception",
        "Spostamenti appuntamenti last minute",
        "No-show costosi e spesso invisibili",
        "Pazienti dormienti che nessuno richiama",
        "Preventivi aperti dimenticati in un cassetto",
        "Agenda sempre piena ma mai ottimizzata",
      ].map((p, i) => (
        <div key={i} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50 flex items-start gap-2 text-sm">
          <X className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
          <span className="text-slate-300">{p}</span>
        </div>
      ))}
    </div>
    <button
      onClick={next}
      className="mt-6 text-cyan-400 hover:text-cyan-300 font-semibold text-sm flex items-center gap-1 mx-auto"
      data-testid="demo-odonto-problem-cta"
    >
      Ti faccio vedere come risolverlo in 2 minuti <ChevronRight className="w-4 h-4" />
    </button>
  </StepCard>
);

// ============================================================
// Step 2 - Calendar (poltrone)
// ============================================================
const StepCalendar = ({ next }) => {
  const hours = ["09:00", "10:00", "11:00", "15:00", "16:00"];
  const [liveAppt, setLiveAppt] = useState(null);
  const [flash, setFlash] = useState(false);

  useEffect(() => {
    const t1 = setTimeout(() => {
      setLiveAppt({
        time: "11:00",
        chair: 1,
        service: "Igiene dentale",
        patient: "Marco Ferri",
        op: 0,
        color: "bg-emerald-200 dark:bg-emerald-800/60 border-emerald-400",
      });
      setFlash(true);
    }, 2500);
    const t2 = setTimeout(() => setFlash(false), 5500);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, []);

  return (
    <StepCard testId="demo-odonto-calendar">
      <StepHeader badge="STEP 4/11" title="Agenda con poltrone, operatori e sale"
        tooltip="Gestisci operatori, sale e poltrone in un'unica agenda." />

      <div className="relative mt-4">
        {/* Live booking banner */}
        <div
          className={`absolute top-0 right-0 z-20 transition-all duration-500 ${
            flash ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-3 pointer-events-none"
          }`}
          data-testid="demo-odonto-live-booking"
        >
          <div className="px-3 py-1.5 rounded-full bg-emerald-500 text-white text-[11px] font-bold shadow-lg flex items-center gap-1.5 animate-pulse">
            <span className="w-1.5 h-1.5 rounded-full bg-white" />
            📲 Nuova prenotazione · Marco Ferri · 11:00
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-slate-700/50 bg-slate-900/60">
        <table className="w-full min-w-[600px] text-xs sm:text-sm">
          <thead className="bg-slate-800/60">
            <tr>
              <th className="p-3 text-left text-slate-400 font-medium">Ora</th>
              {CHAIRS.map(c => (
                <th key={c} className="p-3 text-left text-slate-300 font-medium">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {hours.map(h => (
              <tr key={h} className="border-t border-slate-800/60">
                <td className="p-3 text-slate-500 align-top">{h}</td>
                {CHAIRS.map((_, chairIdx) => {
                  const staticAp = APPOINTMENTS.find(a => a.time === h && a.chair === chairIdx);
                  const liveAp = liveAppt && liveAppt.time === h && liveAppt.chair === chairIdx ? liveAppt : null;
                  const ap = staticAp || liveAp;
                  const isLive = !!liveAp;
                  return (
                    <td key={chairIdx} className="p-1.5 align-top">
                      {ap ? (
                        <div className={`p-2 rounded-lg border-l-4 ${ap.color} text-slate-900 dark:text-slate-100 ${
                          isLive ? "animate-[fadeInScale_0.5s_ease] ring-2 ring-emerald-400 ring-offset-1 ring-offset-slate-900 shadow-lg" : ""
                        }`}>
                          <div className="flex items-center justify-between gap-1">
                            <p className="font-semibold text-xs">{ap.service}</p>
                            {isLive && <span className="text-[8px] font-bold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider">NEW</span>}
                          </div>
                          <p className="text-[10px] opacity-80">{ap.patient}</p>
                          <p className="text-[10px] opacity-60 mt-1">{OPERATORS[ap.op].name}</p>
                        </div>
                      ) : null}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-4 flex-wrap">
        {OPERATORS.map(op => (
          <div key={op.name} className="flex items-center gap-2 text-xs text-slate-400">
            <span className={`w-2.5 h-2.5 rounded-full ${op.color}`} />
            <span>{op.name}</span>
            <span className="text-slate-600">—</span>
            <span className="text-slate-500">{op.role}</span>
          </div>
        ))}
        <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 font-bold flex items-center gap-1 border border-emerald-500/40">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> LIVE · {liveAppt ? "+1 prenotazione" : "in ascolto"}
        </span>
      </div>
    </StepCard>
  );
};

// ============================================================
// Step 3 - Booking flow (patient journey)
// ============================================================
const StepBooking = () => {
  const [phase, setPhase] = useState(1);
  const [chosen, setChosen] = useState(null);
  const dates = ["Dom 27 Apr", "Lun 28 Apr", "Mar 29 Apr", "Mer 30 Apr"];
  const times = ["09:00", "10:30", "11:15", "15:00", "16:30"];
  return (
    <StepCard testId="demo-odonto-booking">
      <StepHeader badge="STEP 5/11" title="Prenotazione online anche di notte"
        tooltip="Nuove prenotazioni anche mentre lo studio è chiuso." />
      <div className="max-w-md mx-auto mt-6 rounded-2xl bg-slate-900 border border-slate-700/50 p-5 shadow-2xl">
        <div className="flex items-center gap-2 mb-4">
          {[1, 2, 3, 4].map(p => (
            <div key={p} className={`h-1.5 flex-1 rounded-full ${phase >= p ? "bg-cyan-500" : "bg-slate-700"}`} />
          ))}
        </div>

        {phase === 1 && (
          <div>
            <p className="text-xs text-slate-400 mb-3">1 · Scegli il servizio</p>
            <div className="space-y-1.5">
              {SERVICES.map((s, i) => (
                <button key={i} onClick={() => { setChosen({ service: s }); setPhase(2); }}
                  className={`w-full p-3 rounded-lg text-left transition-all text-sm flex items-center justify-between ${s.highlight ? "border-2 border-emerald-500 bg-emerald-500/10" : "bg-slate-800/60 hover:bg-slate-700/60 border border-transparent"}`}>
                  <div>
                    <p className="font-medium text-slate-100">{s.name}</p>
                    <p className="text-[10px] text-slate-500">{s.duration} min</p>
                  </div>
                  <span className={`text-xs font-semibold ${s.price === 0 ? "text-emerald-400" : "text-slate-400"}`}>
                    {s.price === 0 ? "GRATIS" : `€${s.price}`}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {phase === 2 && (
          <div>
            <p className="text-xs text-slate-400 mb-3">2 · Scegli la data</p>
            <div className="grid grid-cols-2 gap-2">
              {dates.map((d, i) => (
                <button key={i} onClick={() => { setChosen(prev => ({ ...prev, date: d })); setPhase(3); }}
                  className="p-3 rounded-lg bg-slate-800/60 hover:bg-cyan-500/20 hover:border-cyan-500 border border-transparent text-sm">
                  {d}
                </button>
              ))}
            </div>
          </div>
        )}

        {phase === 3 && (
          <div>
            <p className="text-xs text-slate-400 mb-3">3 · Scegli l'orario</p>
            <div className="grid grid-cols-3 gap-2">
              {times.map((t, i) => (
                <button key={i} onClick={() => { setChosen(prev => ({ ...prev, time: t })); setPhase(4); }}
                  className="p-2.5 rounded-lg bg-slate-800/60 hover:bg-cyan-500/20 hover:border-cyan-500 border border-transparent text-sm">
                  {t}
                </button>
              ))}
            </div>
          </div>
        )}

        {phase === 4 && (
          <div className="text-center py-6 animate-in fade-in">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-3" />
            <p className="font-semibold text-emerald-400">Prenotazione confermata</p>
            <p className="text-sm text-slate-300 mt-1">{chosen?.service?.name}</p>
            <p className="text-xs text-slate-500">{chosen?.date} · {chosen?.time}</p>
            <button onClick={() => { setPhase(1); setChosen(null); }} className="text-xs text-cyan-400 hover:text-cyan-300 mt-3 inline-flex items-center gap-1">
              <RefreshCw className="w-3 h-3" /> Riprova
            </button>
          </div>
        )}
      </div>
    </StepCard>
  );
};

// ============================================================
// Step 4 - WhatsApp AI
// ============================================================
const StepWhatsApp = () => {
  const [msgs, setMsgs] = useState([]);
  useEffect(() => {
    let mounted = true;
    const flow = [
      { who: "u_audio", delay: 900 },
      { who: "typing", delay: 2000 },
      { who: "bot", text: "Certo 😊 Ti propongo:\n\n• domani 18:00\n• venerdì 10:30\n• sabato 09:00\n\nQuale preferisci?", delay: 2500 },
      { who: "u", text: "Venerdì 10:30 va bene!", delay: 4500 },
      { who: "typing", delay: 5200 },
      { who: "bot", text: "Perfetto Anna ✅ Ho spostato la tua igiene a\n*Venerdì 26 Aprile alle 10:30*.\n\nDr.ssa Laura Greco ti aspetta!", delay: 5900 },
    ];
    const LOOP_MS = 8500;
    let timers = [];
    const runCycle = () => {
      if (!mounted) return;
      setMsgs([]);
      timers = flow.map(f =>
        setTimeout(() => setMsgs(m => [...m.filter(x => x.who !== "typing"), f]), f.delay)
      );
    };
    runCycle();
    const loop = setInterval(runCycle, LOOP_MS);
    return () => {
      mounted = false;
      timers.forEach(clearTimeout);
      clearInterval(loop);
    };
  }, []);

  return (
    <StepCard testId="demo-odonto-whatsapp">
      <StepHeader badge="STEP 6/11" title="WhatsApp con AI + supporto audio"
        tooltip="SmartBooker risponde automaticamente anche su WhatsApp." />
      <div className="mx-auto max-w-[360px] mt-6 rounded-3xl bg-slate-900 border border-slate-700/60 overflow-hidden shadow-2xl relative">
        <div className="absolute -top-2 -right-2 z-10 px-2.5 py-1 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold shadow-lg shadow-fuchsia-500/40 flex items-center gap-1 animate-pulse">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-white" /> NEW · AI + Audio
        </div>
        <div className="px-4 py-3 bg-[#075E54] flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-emerald-400 flex items-center justify-center text-white font-bold text-sm">SD</div>
          <div>
            <p className="text-white text-sm font-medium">Studio Dentistico Rossi</p>
            <p className="text-emerald-200 text-[10px]">online</p>
          </div>
        </div>
        <div className="p-4 bg-[#0B141A] min-h-[340px] space-y-2">
          {msgs.map((m, i) => {
            if (m.who === "typing") {
              return <div key={i} className="flex justify-start"><div className="bg-[#1F2C34] rounded-xl px-3 py-2 text-xs text-slate-400 flex items-center gap-1"><span className="w-1 h-1 rounded-full bg-slate-400 animate-bounce" /><span className="w-1 h-1 rounded-full bg-slate-400 animate-bounce delay-75" /><span className="w-1 h-1 rounded-full bg-slate-400 animate-bounce delay-150" /></div></div>;
            }
            if (m.who === "u_audio") {
              return (
                <div key={i} className="flex justify-end animate-in slide-in-from-bottom-1">
                  <div className="max-w-[80%] px-3 py-2 rounded-xl rounded-tr-sm bg-[#005C4B] text-white flex items-center gap-2 text-[12px]">
                    <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center"><Mic className="w-3 h-3" /></span>
                    <span className="flex-1 flex items-center gap-0.5">{[3,7,5,10,6,12,4,9,6,3,7,5,8].map((h,j)=><span key={j} className="inline-block w-0.5 rounded-full bg-white/70" style={{height:`${h}px`}}/>)}</span>
                    <span className="text-[10px] text-white/80">0:05</span>
                  </div>
                </div>
              );
            }
            return (
              <div key={i} className={`flex ${m.who === "u" ? "justify-end" : "justify-start"} animate-in slide-in-from-bottom-1`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-xl text-[12px] leading-relaxed whitespace-pre-line ${m.who === "u" ? "bg-[#005C4B] text-white rounded-tr-sm" : "bg-[#1F2C34] text-slate-200 rounded-tl-sm"}`}>
                  {m.text}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-center text-xs text-slate-400 mt-4 italic">"Ciao vorrei spostare l'igiene di domani" — l'AI capisce anche messaggi vocali.</p>
    </StepCard>
  );
};

// ============================================================
// Step 5 - Reminder
// ============================================================
const StepReminder = () => (
  <StepCard testId="demo-odonto-reminder">
    <StepHeader badge="STEP 7/11" title="Reminder automatici = -37% no-show"
      tooltip="Meno buchi agenda, più produttività." />
    <div className="grid sm:grid-cols-2 gap-4 mt-6">
      <div className="p-5 rounded-2xl bg-slate-800/60 border border-slate-700/50">
        <div className="flex items-center gap-2 text-xs font-semibold text-cyan-400 mb-2"><Clock className="w-4 h-4" /> 24H PRIMA</div>
        <div className="bg-slate-900 rounded-xl p-3 text-sm text-slate-200">
          "Ciao Anna, ti ricordiamo l'appuntamento domani alle 15:00 — Studio Dentistico Rossi"
        </div>
      </div>
      <div className="p-5 rounded-2xl bg-slate-800/60 border border-slate-700/50">
        <div className="flex items-center gap-2 text-xs font-semibold text-amber-400 mb-2"><Bell className="w-4 h-4" /> 2H PRIMA</div>
        <div className="bg-slate-900 rounded-xl p-3 text-sm text-slate-200">
          "Ti aspettiamo tra poco 😊 Confermi la tua igiene dentale?"
        </div>
      </div>
    </div>
    <div className="mt-6 p-5 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 text-center">
      <p className="text-xs text-emerald-300 uppercase tracking-wider">Risultato reale misurato</p>
      <p className="text-4xl sm:text-5xl font-bold text-emerald-400 mt-1">-37%</p>
      <p className="text-sm text-slate-300">no-show</p>
    </div>
  </StepCard>
);

// ============================================================
// Step 6 - Recall automatico
// ============================================================
const StepRecall = () => {
  const [sent, setSent] = useState(false);
  return (
    <StepCard testId="demo-odonto-recall">
      <StepHeader badge="STEP 8/11" title="Recall automatico pazienti inattivi"
        tooltip="Riattiva pazienti senza chiamare uno a uno." />
      <div className="mt-6 space-y-2">
        {INACTIVE_PATIENTS.map((p, i) => (
          <div key={i} className={`p-3 rounded-xl border flex items-center gap-3 transition-colors ${sent ? "bg-emerald-500/10 border-emerald-500/40" : "bg-slate-800/60 border-slate-700/50"}`}>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white text-sm font-bold">{p.avatar}</div>
            <div className="flex-1">
              <p className="font-medium text-sm text-slate-100">{p.name}</p>
              <p className="text-xs text-slate-400">{p.last}</p>
            </div>
            {sent ? <Check className="w-5 h-5 text-emerald-400" /> : <Clock className="w-4 h-4 text-slate-500" />}
          </div>
        ))}
      </div>
      <div className="mt-4 p-4 rounded-2xl bg-slate-900/70 border border-dashed border-slate-700">
        <p className="text-xs text-slate-400 mb-2">Messaggio WhatsApp automatico inviato a tutti:</p>
        <div className="bg-[#1F2C34] text-slate-200 rounded-lg p-3 text-[13px] italic">
          "Ciao Mario 😊 È passato un po' di tempo dall'ultima igiene. Vuoi prenotare? Ti propongo uno slot questa settimana."
        </div>
      </div>
      {!sent && (
        <Button onClick={() => setSent(true)} className="mt-4 w-full rounded-full bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="demo-odonto-recall-btn">
          <MessageSquare className="w-4 h-4 mr-1.5" /> Invia recall WhatsApp a tutti (3)
        </Button>
      )}
      {sent && (
        <div className="mt-4 text-center text-sm text-emerald-400 font-semibold">
          ✅ 3 recall inviati · In media 1 paziente su 3 riprenota
        </div>
      )}
    </StepCard>
  );
};

// ============================================================
// Step 7 - Preventivi
// ============================================================
const StepQuotes = ({ next }) => {
  const total = QUOTES.reduce((s, q) => s + q.amount, 0);
  const topTwo = QUOTES.slice(0, 2);
  return (
    <StepCard testId="demo-odonto-quotes">
      <StepHeader badge="STEP 9/11" title="Preventivi fermi? SmartBooker li richiama per te." />

      {/* Hero result — the "why" in one line */}
      <div className="mt-5 p-5 rounded-2xl bg-gradient-to-br from-emerald-500/15 via-emerald-500/10 to-cyan-500/15 border border-emerald-500/30 text-center">
        <p className="text-xs text-emerald-300 uppercase tracking-wider font-semibold">Recupero potenziale questo mese</p>
        <p className="text-5xl sm:text-6xl font-black bg-gradient-to-r from-emerald-300 to-cyan-300 bg-clip-text text-transparent my-2">
          +€{total.toLocaleString("it-IT")}
        </p>
        <p className="text-xs text-slate-400">
          {QUOTES.length} preventivi aperti · follow-up WhatsApp automatico
        </p>
      </div>

      {/* Compact preview — just 2 recent quotes */}
      <div className="mt-4 space-y-2">
        {topTwo.map((q, i) => (
          <div key={i} className="p-2.5 rounded-xl bg-slate-800/60 border border-slate-700/50 flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white flex-shrink-0">
              <Euro className="w-3.5 h-3.5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-sm text-slate-100 truncate">{q.patient} · <span className="text-slate-400 font-normal">{q.treatment}</span></p>
            </div>
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 flex-shrink-0">
              tra {q.days}gg
            </span>
          </div>
        ))}
      </div>

      {/* Inline CTA — bridge to step 8 */}
      {next && (
        <button
          onClick={next}
          className="mt-5 w-full p-4 rounded-2xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-semibold flex items-center justify-center gap-2 shadow-lg shadow-cyan-500/30 transition-all hover:scale-[1.01]"
          data-testid="demo-odonto-quotes-cta"
        >
          Vedi l'impatto sul tuo studio
          <ArrowRight className="w-4 h-4" />
        </button>
      )}
    </StepCard>
  );
};

// ============================================================
// Step 8 - Analytics
// ============================================================
const StepAnalytics = () => (
  <StepCard testId="demo-odonto-analytics">
    <StepHeader badge="STEP 10/11" title="Ecco cosa cambia nel tuo studio. In 30 giorni." />
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
      {[
        { label: "Nuovi pazienti/mese", value: "+18", icon: Users, color: "from-blue-500 to-cyan-600" },
        { label: "No-show", value: "-37%", icon: Bell, color: "from-amber-500 to-orange-600" },
        { label: "Agenda riempita", value: "+22%", icon: Calendar, color: "from-emerald-500 to-teal-600" },
        { label: "Recall convertiti", value: "14", icon: RefreshCw, color: "from-violet-500 to-fuchsia-600" },
        { label: "Preventivi chiusi", value: "€3.200", icon: CheckCircle2, color: "from-pink-500 to-rose-600" },
        { label: "Entrate generate", value: "€8.460", icon: Wallet, color: "from-indigo-500 to-blue-600" },
      ].map((k, i) => (
        <div key={i} className={`p-5 rounded-2xl bg-gradient-to-br ${k.color} text-white shadow-xl`}>
          <k.icon className="w-6 h-6 opacity-80 mb-2" />
          <p className="text-xs opacity-80">{k.label}</p>
          <p className="text-3xl font-bold mt-1">{k.value}</p>
        </div>
      ))}
    </div>
    <p className="mt-6 text-center text-sm text-slate-300">
      Dati medi dei primi <span className="text-cyan-300 font-semibold">30 giorni</span> sugli studi attivi.
    </p>
  </StepCard>
);

// ============================================================
// Step 9 - Closing (double CTA)
// ============================================================
const StepClosing = ({ clickTrial, clickCall }) => (
  <div className="text-center max-w-3xl mx-auto space-y-6" data-testid="demo-odonto-closing">
    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-xs font-semibold text-amber-300">
      <TrendingUp className="w-3.5 h-3.5" /> CONCLUSIONE
    </div>
    <h2 className="text-3xl sm:text-5xl font-bold leading-tight">
      Se il tuo studio <span className="text-amber-400">dipende ancora dal telefono</span>,
      <br />
      <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">stai perdendo tempo e pazienti.</span>
    </h2>
    <p className="text-lg text-slate-300">
      SmartBooker fa tutto in automatico: agenda, WhatsApp, recall, reminder, preventivi.
    </p>
    <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-4">
      <Button
        onClick={() => clickTrial("closing")}
        className="rounded-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white h-14 px-8 text-base font-semibold shadow-2xl shadow-cyan-500/30"
        data-testid="demo-odonto-trial-closing"
      >
        <Sparkles className="w-5 h-5 mr-2" /> Attiva prova gratuita 14 giorni
      </Button>
      <a
        href="https://wa.me/393770873260?text=Ciao%20vorrei%20una%20demo%20privata%20per%20il%20mio%20studio"
        onClick={() => clickCall("closing")}
        target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center justify-center rounded-full border border-slate-600 bg-slate-800/40 hover:bg-slate-800 text-slate-200 h-14 px-8 text-base font-medium transition-colors"
        data-testid="demo-odonto-call-closing"
      >
        <PhoneCall className="w-5 h-5 mr-2" /> Prenota demo con consulente
      </a>
      <a
        href="/smartbooker-dental-brochure.pdf"
        download="smartbooker-dental-brochure.pdf"
        className="inline-flex items-center justify-center rounded-full border border-slate-600 bg-slate-800/40 hover:bg-slate-800 text-slate-200 h-14 px-8 text-base font-medium transition-colors"
        data-testid="demo-odonto-download-brochure"
      >
        <FileText className="w-5 h-5 mr-2" /> Scarica brochure PDF
      </a>
    </div>
    <div className="flex items-center justify-center gap-4 text-xs text-slate-500 pt-2 flex-wrap">
      <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Nessuna carta richiesta</span>
      <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Attivazione immediata</span>
      <span className="flex items-center gap-1"><Check className="w-3 h-3 text-emerald-500" /> Supporto in italiano</span>
    </div>
    <div className="mt-10 p-6 rounded-3xl bg-gradient-to-br from-slate-800/40 to-slate-900/40 border border-slate-700/40">
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">Pre-configurato per te:</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        {[
          "Odontogramma 32 denti",
          "OCR documenti AI",
          "Firma digitale moduli",
          "Smart Billing SDI-ready",
          "Recall automatici",
          "WhatsApp AI pronto",
          "Reminder anti no-show",
          "Template italiani",
        ].map((t, i) => (
          <div key={i} className="p-2 rounded-lg bg-slate-900/60 border border-slate-700/40 flex items-center gap-1.5">
            <Check className="w-3 h-3 text-emerald-400 flex-shrink-0" />
            <span className="text-slate-300">{t}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

// ============================================================
// Shared layout pieces
// ============================================================
const StepCard = ({ children, testId }) => (
  <div className="rounded-3xl bg-slate-800/30 backdrop-blur-sm border border-slate-700/40 p-6 sm:p-8" data-testid={testId}>
    {children}
  </div>
);

const StepHeader = ({ badge, title, tooltip }) => (
  <div>
    <span className="inline-block text-[10px] font-bold tracking-wider text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded-full border border-cyan-500/20">{badge}</span>
    <h2 className="text-xl sm:text-2xl font-bold mt-2 text-white">{title}</h2>
    {tooltip && <p className="text-sm text-slate-400 mt-1 italic">{tooltip}</p>}
  </div>
);

export default DemoOdonto;
